/*
    This file is for defining custom components used in Builder.IO.
    https://www.builder.io/c/docs/custom-components-setup#registering-your-component
*/

import HeroCard from "../components/ui/molecule/heroCard.vue";
import StoryCarousel from "../components/ui/organism/story/carousel.vue";
import ProductSkuCarousel from "../components/ui/organism/product/carousel.vue";
import ProductCarousel from "../components/ui/organism/product/deprecated-manual-carousel.vue";
import ProductBanner from "../components/ui/organism/product/banner.vue";
import ArticleCarousel from "../components/ui/organism/article/carousel.vue";
import Headline from "../components/ui/atom/headline.vue";
import ValueProposition from "../components/ui/molecule/value-proposition/index.vue";
import Faq from "../components/ui/molecule/faq.vue";
import ProductComparison from "../components/ui/organism/product/comparison.vue";
import PartnerEmailFormCard from "../components/ui/organism/partnerEmailFormCard.vue";

export const loadedComponents = [
        {
            component: HeroCard,
            name: "Hero Card",
            inputs: [
                {
                    name: 'family',
                    type: 'string',
                    defaultValue: 'main',
                    enum: [
                        { label: 'Main', value: 'main' },
                        { label: 'Medium/Secondary', value: 'medium' },
                        { label: 'Short/Small', value: 'short' },
                        { label: 'Full Width with Breadcrumbs', value: 'fullWidthBreadcrumbs' },
                    ],
                },
                {
                    name: 'horizontalPosition',
                    type: 'string',
                    enum: ['left', 'center'],
                    showIf: "!options.get('family') || options.get('family') === 'main'",
                    helperText: 'Align Main hero text and CTAs. Other families use their approved fixed horizontal alignment. Leave unset to retain a saved Header alignment.',
                },
                {
                    name: 'verticalPosition',
                    type: 'string',
                    enum: ['center', 'bottom'],
                    showIf: "options.get('family') !== 'short' && options.get('family') !== 'fullWidthBreadcrumbs'",
                    helperText: 'Position the content vertically: Center or Bottom. Leave unset to retain a saved Header alignment.',
                },
                {
                    name: 'mediaType',
                    type: 'string',
                    defaultValue: 'image',
                    enum: ['image', 'video'],
                    helperText: 'Select image or video. Only the selected media type is displayed. Existing entries without a selection prefer video when supplied.',
                },
                {
                    name: 'background',
                    type: 'string',
                    enum: [
                        { label: 'Dark', value: 'dark' },
                        { label: 'Light', value: 'light' },
                    ],
                    onChange: (options) => {
                        const header = options.get('header');
                        if (header) {
                            if (typeof header.set === 'function') {
                                const updatedHeader = header.set('textColor', '');
                                if (updatedHeader && updatedHeader !== header) {
                                    options.set('header', updatedHeader);
                                }
                            } else {
                                options.set('header', { ...header, textColor: '' });
                            }
                        }
                    },
                    helperText: 'Selecting Dark or Light clears the custom Text color and applies white or black text. Choosing Text color afterward overrides this. Does not change the image or CTA themes.',
                },
                {
                    name: 'imageDecorative',
                    type: 'boolean',
                    defaultValue: true,
                    helperText: 'Turn off when the image communicates information that needs a text description.',
                    showIf: "options.get('mediaType') !== 'video'",
                },
                {
                    name: 'imageAlt',
                    type: 'string',
                    required: true,
                    helperText: 'Describe the informative image. Both device images should convey the same meaning.',
                    showIf: "options.get('mediaType') !== 'video' && options.get('imageDecorative') === false",
                },
                {
                    name: 'breadcrumbs',
                    type: 'list',
                    showIf: "options.get('family') === 'fullWidthBreadcrumbs'",
                    helperText: 'List ancestors in order, followed by the current page. The current page does not need a link.',
                    subFields: [
                        { name: 'label', type: 'string', required: true },
                        { name: 'link', type: 'url' },
                    ],
                },
                {
                    name: 'desktopVideo',
                    type: 'file',
                    allowedFileTypes: ['mp4', 'webm'],
                    showIf: "options.get('mediaType') !== 'image'",
                    defaultValue: '',
                },
                {
                    name: 'mobileVideo',
                    type: 'file',
                    allowedFileTypes: ['mp4', 'webm'],
                    showIf: "options.get('mediaType') !== 'image'",
                    defaultValue: '',
                    helperText: 'Uses the desktop video when omitted. Videos are hidden for reduced-motion preferences; keep the heading and CTAs readable without video.',
                },
                {
                    name: 'tintColor',
                    type: 'color',
                    helperText: 'A color to use as gradient tint overlay starting in top-left and ending transparent at bottom-right of a Video/Image.'
                },
                {
                    name: 'desktopImage',
                    type: 'file',
                    allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'avif'],
                    showIf: "options.get('mediaType') !== 'video'",
                    defaultValue: '',
                },
                {
                    name: 'mobileImage',
                    type: 'file',
                    allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'avif'],
                    showIf: "options.get('mediaType') !== 'video'",
                    defaultValue: '',
                    helperText: 'Uses the desktop image when omitted.'
                },
                {
                    name: 'header',
                    type: 'object',
                    defaultValue: {
                        horizontalPosition: 'left',
                        verticalPosition: 'bottom',
                        textSize: 'medium'
                    },
                    subFields: [
                        {
                            name: 'line1',
                            type: 'string',
                            required: true,
                            helperText: '40 character max.',
                        },
                        {
                            name: 'line2',
                            type: 'string',
                            required: false,
                            helperText: 'Optional legacy second heading line. Keep the combined heading near 40 characters/two lines. Hidden when line 1 is blank.',
                        },
                        {
                            name: 'textSize',
                            type: 'string',
                            enum: ['small', 'medium', 'large', 'XL', '2XL'],
                        },
                        {
                            name: 'textColor',
                            type: 'color',
                            helperText: 'Choosing a color overrides Dark/Light. Selecting Background again clears this color and applies its default. Does not change CTA colors.',
                        },
                        {
                            name: 'topSubtext',
                            type: 'string',
                            helperText: 'Will not display if line 1 is blank.'
                        },
                        {
                            name: 'bottomSubtext',
                            type: 'string',
                            helperText: 'Will not display if line 1 is blank.'
                        },
                        {
                            name: 'buttons',
                            type: 'list',
                            helperText: 'Add up to two CTAs, each with a label and link.',
                            onChange: "if (options.get('buttons')?.length > 2) { options.set('buttons', options.get('buttons').slice(0, 2)); }",

                            subFields: [
                                {
                                    name: 'text',
                                    type: 'string',
                                    required: true
                                },
                                {
                                    name: 'link',
                                    type: 'string',
                                    helperText: 'This should be an internal link. Use /products instead of thorne.com/products',
                                    required: true
                                },
                                {
                                    name: 'buttonTheme',
                                    type: 'string',
                                    enum: ['LIGHT', 'DARK'],
                                    defaultValue: 'LIGHT'
                                },
                                {
                                    name: 'leftIcon',
                                    type: 'string',
                                    helperText: 'Optional icon before the label. Use a regular Font Awesome icon registered by the site. Leave empty for no icon.',
                                },
                                {
                                    name: 'rightIcon',
                                    type: 'string',
                                    helperText: 'Optional icon after the label. Use a registered regular Font Awesome icon. An arrow does not create a dropdown menu.',
                                }
                            ]
                        }
                    ]
                }
            ]
        },

        {
            component: StoryCarousel,
            name: "Story Card",
            inputs: [
                {
                    name: 'theme',
                    type: 'string',
                    enum: ['DARK', 'LIGHT'],
                    defaultValue: 'DARK',
                    required: true
                },
                {
                    name: 'slides',
                    type: 'list',
                    required: true,
                    subFields: [
                        {
                            name: 'header1',
                            type: 'string',
                            required: true,
                            helperText: '40 Characters Max'
                        },
                        {
                            name: 'header2',
                            type: 'string',
                            required: false,
                            helperText: '40 Characters Max'
                        },
                        {
                            name: 'subTitle',
                            type: 'string',
                            required: true,
                            helperText: 'Recommended no more than 100 characters (including spaces)'
                        },
                        {
                            name: 'textPosition',
                            type: 'string',
                            enum: ['LEFT', 'CENTER', 'RIGHT'],
                            defaultValue: 'LEFT',
                            required: true
                        },
                        {
                            name: 'contentFlow',
                            type: 'string',
                            enum: ['NORMAL', 'REVERSE'],
                            defaultValue: 'NORMAL',
                            required: true
                        },
                        {
                            name: 'contentFlowMobile',
                            type: 'string',
                            enum: ['NORMAL', 'REVERSE'],
                            defaultValue: 'NORMAL',
                            required: true
                        },
                        {
                            name: 'callToAction',
                            type: 'object',
                            subFields: [
                                {
                                    name: 'url',
                                    type: 'url',
                                    required: true
                                },
                                {
                                    name: 'label',
                                    type: 'string',
                                    required: true
                                },
                                {
                                    name: 'openInNewTab',
                                    type: 'boolean',
                                    required: true
                                }
                            ]
                        },
                        {
                            name: 'media',
                            type: 'object',
                            subFields: [
                                {
                                    name: 'url',
                                    type: 'url',
                                    required: true
                                },
                                {
                                    name: 'type',
                                    type: 'string',
                                    enum: ['IMAGE', 'VIDEO'],
                                    required: true
                                },
                                {
                                    name: 'altText',
                                    type: 'string',
                                    required: true
                                },
                                {
                                    name: 'tintColor',
                                    type: 'color',
                                    helperText: 'A color to use as gradient tint overlay starting in top-left and ending transparent at bottom-right of a Video/Image.'
                                },
                            ]
                        }
                    ]
                },
            ]
        },

        {
            // Deprecated: This component is no longer used, please use Version 2 instead.
            component: ProductCarousel,
            name: "Product Carousel",
            hideFromInsertMenu: true,
            inputs: [
                {
                    name: 'theme',
                    type: 'string',
                    enum: ['DARK', 'LIGHT'],
                    defaultValue: 'LIGHT',
                    required: true
                },
                {
                    name: 'title',
                    type: 'string',
                    required: false,
                    onChange: (options) => {
                        const MAX_LEN = 40;

                        if (options.get('title').length > MAX_LEN) {
                            alert(`maximum title length is ${MAX_LEN} characters`)
                        }
                    }
                },
                {
                    name: 'products',
                    type: 'list',
                    required: true,
                    subFields: [
                        {
                            name: 'id',
                            type: 'number',
                            required: true,
                        },
                        {
                            name: 'name',
                            type: 'string',
                            required: true,
                        },
                        {
                            name: 'info',
                            type: 'string',
                            required: true
                        },
                        {
                            name: 'price',
                            type: 'string',
                            required: true,
                        },
                        {
                            name: 'rating',
                            type: 'number',
                            required: true,
                            onChange: (options) => {
                                const MAX_STARS = 5;

                                if (options.get('rating') > MAX_STARS || options.get('rating') < 0) {
                                    alert(`rating must be between 0 and ${MAX_STARS}`)
                                }
                            }
                        },
                        {
                            name: 'tag',
                            type: 'string',
                            required: true,
                        },
                    ]
                },
            ]
        },
        {
            component: ProductSkuCarousel,
            name: "v2: Product Carousel",
            inputs: [
                {
                    name: 'theme',
                    type: 'string',
                    enum: ['DARK', 'LIGHT'],
                    defaultValue: 'LIGHT',
                    required: true
                },
                {
                    name: 'title',
                    type: 'string',
                    required: false,
                    onChange: (options) => {
                        const MAX_LEN = 40;

                        if (options.get('title').length > MAX_LEN) {
                            alert(`maximum title length is ${MAX_LEN} characters`)
                        }
                    }
                },
                {
                    name: 'products',
                    type: 'list',
                    required: true,
                    subFields: [
                        {
                            name: 'sku',
                            type: 'string',
                            required: true,
                        },
                        {
                            name: 'tag',
                            type: 'string',
                            required: false,
                        },
                        {
                            name: 'info',
                            type: 'string',
                            required: false,
                        },
                    ]
                }
            ]
        },

        {
            component: ArticleCarousel,
            name: "Article Carousel",
            inputs: [
                {
                    name: 'theme',
                    type: 'string',
                    enum: ['DARK', 'LIGHT'],
                    defaultValue: 'LIGHT',
                    required: true
                },
                {
                    name: 'title',
                    type: 'string',
                    required: false,
                    onChange: (options) => {
                        const MAX_LEN = 40;

                        if (options.get('title').length > MAX_LEN) {
                            alert(`maximum title length is ${MAX_LEN} characters`)
                        }
                    }
                },
                // Note: This is v1 of this component, eventually we'll swap out fields for a plugin where articles can be selected
                {
                    name: 'articles',
                    type: 'list',
                    required: true,
                    subFields: [
                        {
                            name: 'title',
                            type: 'string',
                            required: true
                        },
                        {
                            name: 'url',
                            type: 'url',
                            required: true,
                        },
                        {
                            name: 'img',
                            type: 'url',
                            required: true
                        },
                        {
                            name: 'imgAlt',
                            type: 'string',
                            required: true
                        }
                    ]
                }
            ]
        },

        {
            component: Headline,
            name: "Headline",
            inputs: [
                {
                    name: 'headline',
                    type: 'string',
                    helperText: '40 character max.',
                    defaultValue: '',
                    onChange: (options) => {
                        const MAX_LEN = 40;

                        if (options.get('headline').length > MAX_LEN) {
                            alert(`maximum headline length is ${MAX_LEN} characters`)
                        }
                    }
                },
                {
                    name: 'subHeadline',
                    type: 'string',
                    helperText: '240 character max.',
                    defaultValue: '',
                    onChange: (options) => {
                        const MAX_LEN = 240;

                        if (options.get('subHeadline').length > MAX_LEN) {
                            alert(`maximum sub-headline length is ${MAX_LEN} characters`)
                        }
                    }
                },
                {
                    name: 'callToActionText',
                    type: 'string',
                    helperText: 'If no value is provided, no call to action will be shown.',
                    defaultValue: ''
                },
                {
                    name: 'callToActionUrl',
                    type: 'string',
                    helperText: 'If no value is provided, no call to action will be shown.',
                    defaultValue: ''
                },
                {
                    name: 'callToActionType',
                    type: 'string',
                    enum: ['BUTTON', 'LINK'],
                    defaultValue: 'BUTTON'
                },
                {
                    name: 'theme',
                    type: 'string',
                    enum: ['LIGHT', 'DARK'],
                    defaultValue: 'LIGHT',
                }
            ]
        },

        {
            component: ValueProposition,
            name: 'Value Proposition',
            inputs: [
                {
                    name: 'theme',
                    type: 'string',
                    enum: ['DARK', 'LIGHT'],
                    defaultValue: 'LIGHT',
                    required: true
                },
                {
                    name: 'header',
                    type: 'string',
                    helperText: '40 characters max',
                    required: false,
                    onChange: (options) => {
                        const MAX_LEN = 40;

                        if (options.get('header').length > MAX_LEN) {
                            alert(`Maximum header length is ${MAX_LEN} characters`);
                            options.set('header', options.get('header').slice(0, 40));
                        }
                    }
                },
                {
                    name: 'tiles',
                    type: 'list',
                    required: false,
                    helperText: 'Recommended no more than 4 tiles',
                    subFields: [
                        {
                            name: 'symbol',
                            type: 'string',
                            enum: ['list-check', 'capsules', 'handshake', 'microscope'],
                            helperText: 'If other icon choices are required contact software engineering',
                            required: false
                        },
                        {
                            name: 'style',
                            friendlyName: 'Symbol Style',
                            type: 'string',
                            enum: ['REGULAR', 'SOLID', 'LIGHT'],
                            default: 'LIGHT',
                            helperText: 'If other style choices are required contact software engineering',
                            required: false
                        },
                        {
                            name: 'title',
                            type: 'string',
                            required: true
                        },
                        {
                            name: 'body',
                            type: 'string',
                            helperText: '60 characters max',
                            required: false
                            //onChange doesn't seem to be supported at this time for list subFields - if it becomes available here we should add it for character count
                        },
                        {
                            name: 'url',
                            type: 'url',
                            required: false,
                        },
                    ]
                }
            ]
        },

        {
            component: Faq,
            name: 'FAQ',
            inputs: [
                {
                    name: 'theme',
                    type: 'string',
                    enum: ['DARK', 'LIGHT'],
                    defaultValue: 'LIGHT',
                    required: true
                },
                {
                    name: 'faqs',
                    type: 'list',
                    required: true,
                    subFields: [
                        {
                            name: 'question',
                            type: 'string',
                            helperText: '40 characters max',
                            required: true
                            //onChange doesn't seem to be supported at this time for list subFields - if it becomes available here we should add it for character count
                        },
                        {
                            name: 'answer',
                            type: 'string',
                            required: true
                            //onChange doesn't seem to be supported at this time for list subFields - if it becomes available here we should add it for character count
                        }
                    ]
                }
            ]
        },

        {
            component: ProductBanner,
            name: 'Product Banner',
            inputs: [
                {
                    name: 'theme',
                    type: 'string',
                    enum: ['DARK', 'LIGHT'],
                    defaultValue: 'LIGHT'
                },

                {
                    name: 'contentFlow',
                    type: 'string',
                    enum: ['NORMAL', 'REVERSE'],
                    defaultValue: 'NORMAL'
                },

                {
                    name: 'contentAlignment',
                    type: 'string',
                    enum: ['LEFT', 'CENTER'],
                    defaultValue: 'LEFT'
                },

                {
                    name: 'productSku',
                    type: 'string',
                    required: true
                },

                {
                    name: 'img',
                    type: 'url',
                    helperText: 'Image to override the primary product image.'
                },

                {
                    name: 'showAddToCartButton',
                    type: 'boolean',
                    defaultValue: true
                }
            ]
        },
        {
            component: PartnerEmailFormCard,
            name: 'Partner Email Form Card',
            inputs: [
                {
                    name: 'buttonText',
                    type: 'string',
                    required: true,
                },
                {
                    name: 'successMessage',
                    type: 'string',
                    required: true,
                },
                {
                    name: 'errorMessage',
                    type: 'string',
                    required: true,
                },
                {
                    name: 'url',
                    type: 'url',
                    required: true,
                },
                {
                    name: 'methodType',
                    type: 'string',
                    enum: ['GET', 'POST'],
                    defaultValue: 'GET'
                },
                {
                    name: 'urlParams',
                    type: 'list',
                    subFields: [
                        {
                            name: 'key',
                            type: 'string',
                            required: true,
                            helperText: "This should be the key of the query parameter."
                        },
                        {
                            name: 'value',
                            type: 'string',
                            required: true,
                            helperTest: "This should be the value of the query parameter."
                        }
                    ]
                },
                {
                    name: 'bodyData',
                    type: 'string',
                },
            ]
        },
        {
            component: ProductComparison,
            name: 'Product Comparison',

            inputs: [
                {
                    name: 'theme',
                    type: 'string',
                    enum: ['DARK', 'LIGHT'],
                    defaultValue: 'LIGHT'
                },
                
                {
                    name: 'title',
                    type: 'string',
                    required: true,

                    onChange: (options) => {
                        const MAX_LEN = 50;
                        const title: string = options.get('title');

                        if (title.length > MAX_LEN) {
                            options.set('title', title.substring(0, MAX_LEN));
                            alert(`Maximum title length is ${MAX_LEN} characters`);
                        }
                    }
                },

                {
                    name: 'rows',
                    type: 'list',
                    required: true,
                    helperText: 'Rows used to represent value statements',

                    subFields: [
                        {
                            name: 'rowTitle',
                            type: 'string',
                            required: true,
                        }
                    ]
                },

                {
                    name: 'products',
                    type: 'list',
                    required: true,

                    onChange: (options) => {
                        const MAX_PRODUCTS = 4;
                        const products = options.get('products');

                        if (products.length > MAX_PRODUCTS) {
                            options.set('products', products.slice(0, MAX_PRODUCTS));
                            alert(`Products are limited to ${MAX_PRODUCTS} maximum`);
                        }
                    },

                    subFields: [
                        {
                            name: 'sku',
                            type: 'string',
                            required: true
                        },
                        {
                            name: 'productRows',
                            type: 'list',
                            required: true,
                            helperText: 'Product specific value statements, maps to previously defined rows',

                            subFields: [
                                {
                                    name: 'value',
                                    type: 'richText',
                                    required: true
                                }
                            ]
                        }
                    ]
                },
            ]
        }
];
